import { useEffect, useContext, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/esm/Button";
import Col from "react-bootstrap/esm/Col";
import Row from "react-bootstrap/esm/Row";
import Form from "react-bootstrap/Form";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import { Link, useParams } from "react-router-dom";
import ContactSection from "./createCompetitionForm/ContactSection";
import BasicInformationSection from "./createCompetitionForm/BasicInformationSection";
import RegulationSection from "./createCompetitionForm/RegulationSection";
import OfficialsSection from "./createCompetitionForm/OfficialsSection";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import {
  updateCompetition,
  getCompetition,
  cancelCompetition,
} from "../../api/Competitions";
import { UserContext } from "../../App";
import CompetitionStatus from "./CompetitionStatus";
import NavigationSection from "./createCompetitionForm/NavigationSection";
import NavMenu from "../navMenu/NavMenu";
import Loading from "../../components/Loading";
import CompetitionDetailsHeader from "./CompetitionDetailsHeader";
import ModalCentered from "../../components/ModalCentered";
import CustomToast from "../../components/CustomToast";

function CompetitionDetails() {
  const [competition, setCompetition] = useState({});
  const [competitionName, setCompetitionName] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [updatedTime, setUpdatedTime] = useState("");
  const [published, setPublished] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [showPublishError, setShowPublishError] = useState(false);
  const [confirmPublishModalShow, setConfirmPublishModalShow] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [toastProps, setToastProps] = useState({
    show: false,
  });
  const userContext = useContext(UserContext);
  const { id: competitionId } = useParams();

  useEffect(() => {
    async function fetchCompetition() {
      const result = await getCompetition(competitionId);
      if (result) {
        setCompetitionName(result.competition.basicInformation?.name);
        setCompetition(result.competition);
        setIsLoaded(true);
        const isPublished = result.competition?.status !== "Utkast";
        setPublished(isPublished);
      }
    }

    fetchCompetition();
  }, []);

  useEffect(() => {
    const isPublished = competition?.status !== "Utkast";
    setPublished(isPublished);
    setCompetitionName(competition.basicInformation?.name);
    setIsLoaded(true);
  }, [competition]);

  const handleCancelCompetition = async () => {
    const result = await cancelCompetition(competitionId);

    if (result.statusCode === 200) {
      competition.status = "Inställd";
      setShowCancelModal(false);
    }
  };

  const minDate = new Date("2000-01-01");
  const maxDate = new Date("2099-01-01");

  const createCompetitionSchema = Yup.object().shape({
    BasicInformation: Yup.object().shape({
      Name: Yup.string().required("Ange tävlingsnamn"),
      StartDate: Yup.date()
        .required("Ange startdatum")
        .min(
          minDate,
          `Startdatum kan inte vara före ${minDate.toISOString().split("T")[0]}`
        )
        .max(
          maxDate,
          `Startdatum kan inte vara efter ${
            maxDate.toISOString().split("T")[0]
          }`
        ),

      EndDate: Yup.date()
        .min(Yup.ref("StartDate"), "Slutdatum kan inte vara före startdatum")
        .max(
          maxDate,
          `Slutdatum kan inte vara efter ${maxDate.toISOString().split("T")[0]}`
        )
        .required("Ange slutdatum"),
      RegistrationDeadline: Yup.date()
        .min(
          minDate,
          `Sista anmälningsdag kan inte vara före ${
            minDate.toISOString().split("T")[0]
          }`
        )
        .max(
          maxDate,
          `Sista anmälningsdag kan inte vara efter ${
            maxDate.toISOString().split("T")[0]
          }`
        )
        .when("StartDate", (StartDate) => {
          if (StartDate) {
            return Yup.date().test(
              "check-permission",
              "Sista anmälningsdag måste vara senast 48 timmar innan startdatum",
              function (value) {
                return !(StartDate - value < 172800000);
              }
            );
          }
        })
        .required("Ange sista anmälningsdag"),
      Venue: Yup.string().required("Ange tävlingsplats"),
    }),
    Officials: Yup.object().shape({
      CompetitionManager: Yup.string().required("Ange en tävlingsledare"),
      AssistantCompetitionManager: Yup.string().required(
        "Ange en biträdande tävlingsledare"
      ),
      RaceController: Yup.string().required("Ange en racecontroller"),
      Judge: Yup.string().required("Ange en domare"),
      TechnicalManager: Yup.string().required("Ange en teknisk chef"),
      SecurityManager: Yup.string().required("Ange en säkerhetschef"),
      EnvironmentManager: Yup.string().required("Ange en miljöchef"),
    }),
    Contacts: Yup.object().shape({
      Disclosures: Yup.object().shape({
        Name: Yup.string().required("Ange ett namn"),
        PhoneNumber: Yup.string().test(
          "oneOfRequired",
          "Ett av fälten telefon eller e-post måste anges",
          function (item) {
            return this.parent.PhoneNumber || this.parent.Email;
          }
        ),
        Email: Yup.string()
          .email("Ange en giltig epost")
          .test(
            "oneOfRequired",
            "Ett av fälten telefon eller e-post måste anges",
            function (item) {
              return this.parent.PhoneNumber || this.parent.Email;
            }
          ),
      }),
      Cancellations: Yup.object().shape({
        Name: Yup.string().required("Ange ett namn"),
        PhoneNumber: Yup.string().test(
          "oneOfRequiredCancellations",
          "Ett av fälten telefon eller e-post måste anges",
          function (item) {
            return this.parent.PhoneNumber || this.parent.Email;
          }
        ),
        Email: Yup.string()
          .email("Ange en giltig epost")
          .test(
            "oneOfRequiredCancellations",
            "Ett av fälten telefon eller e-post måste anges",
            function (item) {
              return this.parent.PhoneNumber || this.parent.Email;
            }
          ),
      }),
    }),
    Regulation: Yup.object().shape({
      RaceClasses: Yup.array().required().min(1, "Välj minst en klass"),
      ResultAssigned: Yup.string().required("Fyll i fältet"),
      TimeTable: Yup.string().required("Ange tidsplan"),
      CompetitionImplementation: Yup.string().required(
        "Ange tävlingens genomförande"
      ),
      StartOrder: Yup.string().required("Ange startordning"),
      StartLineup: Yup.string().required("Ange startuppställning"),
      EnvironmentRegulation: Yup.string().required("Ange miljö"),
      FalseStartAction: Yup.string().required("Ange åtgärd vid tjuvstart"),
      TireLimitation: Yup.string().required("Ange däckbeskrivning"),
      Fee: Yup.string().required("Ange avgifter"),
      Commercials: Yup.string().required("Ange reklam"),
      Prizes: Yup.string(),
      MaxNbrOfParticipants: Yup.number(),
    }),
  });

  const statusesWithNav = ["Kommande", "Avslutad", "Pågående"];

  return (
    <div>
      <ToastContainer position="bottom-start" className="position-fixed p-3">
        <Toast
          bg="success"
          onClose={() => setShowPublishSuccess(false)}
          show={showPublishSuccess}
          delay={10000}
          autohide
        >
          <Toast.Header className="d-flex justify-content-between">
            <span>Publiceringen lyckades!</span>
          </Toast.Header>
          <Toast.Body>Tävlingen är nu publicerad.</Toast.Body>
        </Toast>
      </ToastContainer>

      <ToastContainer position="bottom-start" className="position-fixed p-3">
        <Toast
          bg="danger"
          onClose={() => setShowPublishError(false)}
          show={showPublishError}
          delay={10000}
          autohide
        >
          <Toast.Header className="d-flex justify-content-between">
            <span>Publiceringen misslyckades</span>
          </Toast.Header>
          <Toast.Body>
            Kunde inte publicera tävlingen. Vänligen kolla så att alla fält är
            ifyllda korrekt.
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <Row>
        <Col>
          <div className="d-md-flex gap-md-4 gap-lg-5">
            <div className="d-flex align-items-center mb-2 mb-md-0">
              <Link
                to="/organizer/competitions"
                className="text-reset text-decoration-none"
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px" }}
                >
                  arrow_back_ios
                </span>
                <span>Tillbaka</span>
              </Link>
            </div>
            <div className="d-flex align-items-center gap-4 gap-md-4 gap-lg-5">
              <h2 className="mb-0">
                {competitionName && competitionName.length > 0
                  ? competitionName
                  : "Namnlöst utkast"}
              </h2>

              <CompetitionStatus
                status={competition?.status}
                id={competitionId}
              />
            </div>
          </div>
        </Col>
      </Row>
      {statusesWithNav.includes(competition?.status) ? (
        <Row>
          <Col>
            <NavMenu competition={competition} />
          </Col>
        </Row>
      ) : (
        <Row className="mb-4"></Row>
      )}
      <Row>
        <Col className="d-flex flex-column flex-md-row justify-content-between">
          <CompetitionDetailsHeader
            isLoaded={isLoaded}
            published={published}
            setConfirmPublishModalShow={setConfirmPublishModalShow}
            updatedTime={updatedTime}
            competitionStatus={competition?.status}
            setShowCancelModal={setShowCancelModal}
            setCompetition={setCompetition}
            setToastProps={setToastProps}
          />
        </Col>
      </Row>
      <Row>
        <NavigationSection />
        <Col md={9} xxl={10}>
          {isLoaded ? (
            <Formik
              initialValues={{
                CompetitionId: competitionId,
                OrganizerId: userContext.user?.id,
                BasicInformation: {
                  Name: competition?.basicInformation?.name ?? "",
                  Venue: competition?.basicInformation?.venue ?? "",
                  StartDate: competition?.basicInformation?.startDate
                    ? new Date(
                        competition.basicInformation.startDate
                      ).toLocaleDateString("sv-SE")
                    : "",
                  EndDate: competition?.basicInformation?.endDate
                    ? new Date(
                        competition.basicInformation.endDate
                      ).toLocaleDateString("sv-SE")
                    : "",
                  RegistrationDeadline: competition?.basicInformation
                    ?.registrationDeadline
                    ? new Date(
                        competition.basicInformation.registrationDeadline
                      ).toLocaleDateString("sv-SE")
                    : "",
                },
                Officials: {
                  CompetitionManager:
                    competition?.officials?.competitionManager ?? "",
                  AssistantCompetitionManager:
                    competition?.officials?.assistantCompetitionManager ?? "",
                  RaceController: competition?.officials?.raceController ?? "",
                  Judge: competition?.officials?.judge ?? "",
                  TechnicalManager:
                    competition?.officials?.technicalManager ?? "",
                  SecurityManager:
                    competition?.officials?.securityManager ?? "",
                  EnvironmentManager:
                    competition?.officials?.environmentManager ?? "",
                },
                Contacts: {
                  Disclosures: {
                    Name: competition?.contacts?.disclosures?.name ?? "",
                    PhoneNumber:
                      competition?.contacts?.disclosures?.phoneNumber ?? "",
                    Email: competition?.contacts?.disclosures?.email ?? "",
                  },
                  Cancellations: {
                    Name: competition?.contacts?.cancellations?.name ?? "",
                    PhoneNumber:
                      competition?.contacts?.cancellations?.phoneNumber ?? "",
                    Email: competition?.contacts?.cancellations?.email ?? "",
                  },
                },
                Regulation: {
                  CompetitionForms:
                    competition.regulation?.competitionForms ?? "traditional",
                  RaceClasses: competition.regulation?.raceClasses ?? [],
                  MaxNbrOfParticipants:
                    competition?.regulation?.maxNbrOfParticipants ?? 0,
                  TimeTable: competition?.regulation?.timeTable ?? "",
                  CompetitionImplementation:
                    competition?.regulation?.competitionImplementation ?? "",
                  ResultAssigned: competition?.regulation?.resultAssigned ?? "",
                  StartOrder: competition?.regulation?.startOrder ?? "",
                  StartLineup: competition?.regulation?.startLineup ?? "",
                  EnvironmentRegulation:
                    competition?.regulation?.environmentRegulation ?? "",
                  FalseStartAction:
                    competition?.regulation?.falseStartAction ?? "",
                  TireLimitation: competition?.regulation?.tireLimitation ?? "",
                  Fee: competition?.regulation?.fee ?? "",
                  Commercials: competition?.regulation?.commercials ?? "",
                  Prizes: competition?.regulation?.prizes ?? "",
                },
                isDraft: true,
                Miscellaneous: "",
              }}
              onSubmit={async (values) => {
                const publish = true;

                const success = await updateCompetition(values, publish);

                if (success) {
                  setPublished(true);
                  setShowPublishSuccess(true);
                  setShowPublishError(false);
                  competition.status = "Kommande";
                } else {
                  setShowPublishSuccess(false);
                  setShowPublishError(true);
                }
              }}
              validationSchema={createCompetitionSchema}
              enableReinitialize
              validateOnChange={false}
              validateOnBlur={false}
            >
              {({
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                touched,
                isValid,
                errors,
                validateForm,
              }) => (
                <FormikForm
                  noValidate
                  // validated={validated}
                  id="form-create-competition"
                  onSubmit={handleSubmit}
                  onBlur={async () => {
                    const time = await updateCompetition(values);
                    setUpdatedTime(time);
                  }}
                >
                  <BasicInformationSection
                    values={values}
                    handleChange={handleChange}
                    handleNameChange={setCompetitionName}
                    competitionName={competitionName}
                    handleBlur={handleBlur}
                    errors={errors}
                    disabled={published}
                  />

                  <RegulationSection
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors}
                    disabled={published}
                  />

                  <OfficialsSection
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors}
                    disabled={published}
                  />

                  <ContactSection
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors}
                    disabled={published}
                  />

                  <h5 id="section-other-info" className="mt-4">
                    Övrig information
                  </h5>

                  <Form.Group controlId="formOtherInfo">
                    <Form.Label>(frivilligt)</Form.Label>
                    <Form.Control
                      as="textarea"
                      className="p-2"
                      placeholder="Ange övrig information"
                      disabled={published}
                    />
                  </Form.Group>
                </FormikForm>
              )}
            </Formik>
          ) : (
            <div className="d-flex justify-content-center align-items-center mt-5">
              <Loading size="100px" text="Laddar formulär" />
            </div>
          )}
        </Col>
      </Row>

      {!published && isLoaded && (
        <Row className="mt-5">
          <Col className="d-flex flex-column-reverse flex-md-row justify-content-md-end align-items-md-center">
            <span className="text-secondary-black text-sm me-3 small align-self-md-center justify-self-md-center text-center text-md-unset mt-2 mt-md-0">
              {updatedTime ? "Utkast sparat " + updatedTime : ""}
            </span>
            <Button onClick={() => setConfirmPublishModalShow(true)}>
              Publicera inbjudan
            </Button>
          </Col>
        </Row>
      )}

      <Modal
        show={confirmPublishModalShow}
        onHide={() => setConfirmPublishModalShow(false)}
        centered
        aria-labelledby="contained-modal-title-vcenter"
        className="p-3"
      >
        <Modal.Header closeButton>
          <Modal.Title>Bekräfta publicering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Är du säker på att du vill publicera tävlingen? Denna åtgärd går ej
          att ångra.
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            onClick={() => setConfirmPublishModalShow(false)}
            variant="outline-secondary"
          >
            Avbryt
          </Button>
          <Button
            type="submit"
            form="form-create-competition"
            onClick={() => setConfirmPublishModalShow(false)}
          >
            Publicera
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalCentered
        show={showCancelModal}
        confirmBtnText="Fortsätt"
        header="Är du säker på att du vill ställa in tävlingen?"
        body="Du är nu på väg att ställa in tävlingen. Detta går ej att ångra."
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancelCompetition}
      />

      <CustomToast
        show={toastProps?.show}
        body={toastProps?.body}
        title={toastProps?.title}
        type={toastProps?.type}
        onClose={() => setToastProps({ ...toastProps, show: false })}
      />
    </div>
  );
}

export default CompetitionDetails;
